import { useEffect, useMemo, useState } from "react";
import { Search, Check, Users, Building2, UserRound } from "lucide-react";
import { api, mediaUrl } from "../../lib/api";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";

// Selector de audiencia estándar (Regla G-001): toda la empresa, por áreas, o
// usuarios específicos elegidos SIEMPRE desde el directorio (nunca a mano).
// value: { audience: 'all'|'areas'|'users', user_ids: [], areas: [] }
export default function AudiencePicker({ value, onChange, areas = [], testid = "audience" }) {
  const [employees, setEmployees] = useState([]);
  const [q, setQ] = useState("");
  const v = value || { audience: "all", user_ids: [], areas: [] };

  useEffect(() => {
    api.get("/directory").then((r) => setEmployees(r.data.employees || [])).catch(() => setEmployees([]));
  }, []);

  const set = (patch) => onChange({ ...v, ...patch });
  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return employees;
    return employees.filter((e) => (e.name || "").toLowerCase().includes(t) || (e.puesto || "").toLowerCase().includes(t) || (e.area || "").toLowerCase().includes(t));
  }, [q, employees]);

  const toggleUser = (id) => {
    const s = new Set(v.user_ids || []);
    s.has(id) ? s.delete(id) : s.add(id);
    set({ user_ids: [...s] });
  };
  const toggleArea = (a) => {
    const s = new Set(v.areas || []);
    s.has(a) ? s.delete(a) : s.add(a);
    set({ areas: [...s] });
  };

  const OPTS = [
    ["all", "Toda la empresa", Building2],
    ["areas", "Por áreas", Users],
    ["users", "Usuarios específicos", UserRound],
  ];

  return (
    <div className="space-y-3" data-testid={`${testid}-picker`}>
      <div className="flex flex-wrap gap-2">
        {OPTS.map(([k, l, Ic]) => (
          <button
            key={k}
            type="button"
            onClick={() => set({ audience: k })}
            data-testid={`${testid}-mode-${k}`}
            className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm border transition-colors ${v.audience === k ? "border-violet bg-violet/10 text-violet" : "border-navy/15 text-navy/60 hover:border-violet/40"}`}
          >
            <Ic className="w-4 h-4" /> {l}
          </button>
        ))}
      </div>

      {v.audience === "areas" && (
        <div className="flex flex-wrap gap-2" data-testid={`${testid}-areas`}>
          {areas.length === 0 && <span className="text-xs text-navy/40">No hay áreas registradas.</span>}
          {areas.map((a) => (
            <button
              key={a}
              type="button"
              onClick={() => toggleArea(a)}
              data-testid={`${testid}-area-${a}`}
              className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${(v.areas || []).includes(a) ? "border-violet bg-violet text-white" : "border-navy/15 text-navy/60 hover:border-violet/40"}`}
            >
              {a}
            </button>
          ))}
        </div>
      )}

      {v.audience === "users" && (
        <div className="border border-navy/15 rounded-xl overflow-hidden">
          <div className="flex items-center gap-2 border-b border-navy/10 px-3 py-2">
            <Search className="w-4 h-4 text-navy/40" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre, cargo o área…" data-testid={`${testid}-search`} className="flex-1 bg-transparent text-sm outline-none placeholder:text-navy/40" />
            <span className="text-xs text-navy/45" data-testid={`${testid}-count`}>{(v.user_ids || []).length} seleccionados</span>
          </div>
          <div className="max-h-56 overflow-y-auto py-1">
            {filtered.map((e) => {
              const sel = (v.user_ids || []).includes(e.id);
              return (
                <button
                  key={e.id}
                  type="button"
                  onClick={() => toggleUser(e.id)}
                  data-testid={`${testid}-user-${e.id}`}
                  className={`flex w-full items-center gap-2.5 px-3 py-2 text-left transition-colors ${sel ? "bg-violet/5" : "hover:bg-navy/[0.03]"}`}
                >
                  <Avatar className="h-8 w-8 shrink-0">
                    {e.avatar ? <AvatarImage src={mediaUrl(e.avatar)} alt={e.name} /> : null}
                    <AvatarFallback className="text-[10px] bg-violet/10 text-violet">{(e.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("")}</AvatarFallback>
                  </Avatar>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-navy">{e.name}</span>
                    <span className="block truncate text-xs text-navy/50">{[e.puesto, e.area].filter(Boolean).join(" · ") || "—"}</span>
                  </span>
                  {sel && <Check className="w-4 h-4 text-violet shrink-0" />}
                </button>
              );
            })}
            {filtered.length === 0 && <div className="px-3 py-4 text-sm text-navy/40">Sin coincidencias.</div>}
          </div>
        </div>
      )}
    </div>
  );
}
